import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import classNames from 'clsx';
import { Alert, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AddIcon from '@mui/icons-material/Add';
import { RRuleSet, RRule } from 'rrule';
export { RRuleSet, RRule };
import { ViewState } from '@devexpress/dx-react-scheduler';
import {
  Scheduler,
  MonthView,
  DayView,
  Toolbar,
  DateNavigator,
  Appointments,
  TodayButton,
  ViewSwitcher,
  AllDayPanel,
} from '@devexpress/dx-react-scheduler-material-ui';

const useStyles = makeStyles()((theme) => ({
  layout: {
    height: '100%',
    '& > *:only-child': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      '& > *:last-child': {
        flex: 1,
        '& table': {
          height: '100%',
        },
      },
    },
  },
  dayLayout: {
    '& > *:only-child': {
      display: 'flex',
      flexDirection: 'column',
      '& > *:last-child': {
        overflow: 'auto',
        maxHeight: '600px',
      },
    },
  },
  text: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  content: {
    opacity: 0.7,
  },
  container: {
    width: '100%',
    lineHeight: 1.2,
    height: '100%',
  },
  appointment: {},
  appointmentContent: {
    padding: '1px 2px',
    cursor: 'pointer',
  },
  actionButton: {
    display: 'flex',
    flex: '1 0 0',
    justifyContent: 'flex-end',
    [theme.breakpoints.up('md')]: {
      width: 'max-content',
    },
  },
}));

const MonthLayout = (props: any) => {
  const MonthViewLayout = (MonthView as any)?.Layout;
  const { classes } = useStyles();

  if (!MonthViewLayout) return null;

  return <MonthViewLayout {...props} className={classes.layout} />;
};

const DayLayout = (props: any) => {
  const DayViewLayout = (DayView as any)?.Layout;
  const { classes } = useStyles();

  if (!DayViewLayout) return null;

  return <DayViewLayout {...props} className={classes.dayLayout} />;
};

const AppointmentContent = (props: any) => {
  const AppointmentContentComponent = (Appointments as any)?.AppointmentContent;
  const { data } = props;
  const { classes } = useStyles();

  if (!AppointmentContentComponent) return null;

  return (
    <AppointmentContentComponent
      className={classes.appointmentContent}
      {...props}
    >
      <div className={classes.container}>
        <div className={classes.text}>
          {data.title}
        </div>
        <div className={classNames(classes.text, classes.content)}>
          {`Lieu: ${data.location}`}
        </div>
      </div>
    </AppointmentContentComponent>
  );
};

const Appointment = (props: any) => {
  const AppointmentComponent = (Appointments as any)?.Appointment;
  const { data } = props;
  const router = useRouter();
  const handleClick = useCallback(() => {
    router.push(`/evenement/${data.url}`);
  }, [router, data]);

  if (!AppointmentComponent) return null;

  return (
    <AppointmentComponent
      onClick={handleClick}
      style={{ backgroundColor: data.backgroundColor + '!important' }}
      {...props}
    />
  );
};

interface FlexibleEmptySpaceAddEventProps {
  actor?: { id: string | number };
}

const FlexibleEmptySpaceAddEvent = ({ actor }: FlexibleEmptySpaceAddEventProps) => {
  const ToolbarFlexibleSpace = (Toolbar as any)?.FlexibleSpace;
  const { classes } = useStyles();
  const router = useRouter();

  const handleClickAddEvent = useCallback(() => {
    router.push(`/admin/events/add?actorId=${actor?.id}`);
  }, [router, actor]);

  if (!ToolbarFlexibleSpace) return null;

  return (
    <ToolbarFlexibleSpace>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<AddIcon />}
        onClick={handleClickAddEvent}
        className={classes.actionButton}
      >
        Ajouter un évènement
      </Button>
    </ToolbarFlexibleSpace>
  );
};

interface CalendarEvent {
  id?: string | number;
  title?: string;
  startDate: Date;
  endDate?: Date;
  url?: string;
  location?: string;
  backgroundColor?: string;
  rRule?: string;
  [key: string]: any;
}

interface CalendarProps {
  events?: CalendarEvent[];
  withViewSwitcher?: boolean;
  withAddEvent?: boolean;
  className?: string;
  actor?: { id: string | number };
  startDate?: Date;
}

const Calendar = ({
  events = [],
  withViewSwitcher = true,
  withAddEvent = false,
  className,
  actor,
  startDate,
}: CalendarProps) => {
  const hasScheduler = Boolean(Scheduler);
  const hasViewState = Boolean(ViewState);
  const hasMonthView = Boolean(MonthView);
  const hasAppointments = Boolean(Appointments);
  const hasToolbar = Boolean(Toolbar);
  const hasDateNavigator = Boolean(DateNavigator);
  const hasTodayButton = Boolean(TodayButton);
  const hasDayView = Boolean(DayView);
  const hasAllDayPanel = Boolean(AllDayPanel);
  const hasViewSwitcher = Boolean(ViewSwitcher);

  const toolbarProps = withAddEvent && (Toolbar as any)?.FlexibleSpace
    ? { flexibleSpaceComponent: () => <FlexibleEmptySpaceAddEvent actor={actor} /> }
    : {};

  const defaultMessages = {
    allDay: 'Toute la journée',
  };

  const RootComponent = useCallback((rootComponentProps: any) => {
    return (
      <div {...rootComponentProps} className={className} />
    );
  }, [className]);

  if (!hasScheduler || !hasViewState || !hasMonthView || !hasAppointments) {
    console.error('Calendar unavailable: missing required DevExpress components', {
      hasScheduler,
      hasViewState,
      hasMonthView,
      hasAppointments,
    });

    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Le calendrier est indisponible pour le moment.
      </Alert>
    );
  }

  return (
    <Scheduler
      firstDayOfWeek={1}
      locale="fr-FR"
      RootProps={{
        height: 'auto',
      }}
      data={events}
      rootComponent={RootComponent}
    >
      {hasViewState && (
        <ViewState
          defaultCurrentDate={startDate !== undefined ? startDate : new Date()}
        />
      )}

      {hasToolbar && (
        <Toolbar
          {...toolbarProps}
        />
      )}

      {hasDateNavigator && <DateNavigator />}
      {hasTodayButton && <TodayButton messages={{ today: "Aujourd'hui" }} />}

      {hasMonthView && (
        <MonthView
          layoutComponent={MonthLayout}
          displayName="Mois"
        />
      )}
      {hasDayView && (
        <DayView
          displayName="Semaine"
          startDayHour={8}
          intervalCount={7}
          layoutComponent={DayLayout}
        />
      )}
      {hasAllDayPanel && (
        <AllDayPanel
          messages={defaultMessages}
        />
      )}
      {hasAppointments && (
        <Appointments
          appointmentComponent={Appointment}
          appointmentContentComponent={AppointmentContent}
        />
      )}
      {withViewSwitcher && hasViewSwitcher && <ViewSwitcher />}
    </Scheduler>
  );
};

export default Calendar;
